// Cadastro de empresa: ao completar CNPJ ou CEP, o servidor consulta a fonte
// pública e o formulário só recebe sugestões. Nada é gravado até o envio.

type Kind = 'cnpj' | 'cep';

type Failure = { titulo?: string; corpo?: string; proximo_passo?: string; correlation_id?: string };

const LENGTH: Record<Kind, number> = { cnpj: 14, cep: 8 };

const COPY = {
  cnpj: {
    incompleto: 'Digite os 14 caracteres do CNPJ para buscar os dados.',
    buscando: 'Consultando o CNPJ na Receita Federal…',
    pronto: 'Preenchemos os dados da Receita Federal. Campos que você já tinha digitado ficaram como estavam. Confira tudo antes de gravar.',
  },
  cep: {
    incompleto: 'Digite os 8 dígitos do CEP para buscar o endereço.',
    buscando: 'Consultando o CEP…',
    pronto: 'Preenchemos o endereço e o código IBGE pelo CEP. Confira e informe número e complemento.',
  },
  semResposta: 'A consulta não respondeu. Preencha os dados manualmente; o cadastro não depende dela.',
  sessao: 'Sua sessão terminou, por isso não consultamos. Entre de novo para continuar o cadastro.',
} as const;

function normalize(kind: Kind, raw: string): string {
  return kind === 'cnpj' ? raw.toUpperCase().replace(/[^0-9A-Z]/g, '') : raw.replace(/\D/g, '');
}

function show(status: HTMLElement, text: string, error = false): void {
  status.hidden = false;
  status.className = error ? 'msg-error' : 'field-hint';
  status.textContent = text;
}

// Campo preenchido pela consulta fica marcado até a pessoa editá-lo. Uma nova
// consulta substitui (ou limpa) o que está vazio ou marcado; o CEP manda no
// próprio endereço e substitui sempre.
function fill(form: HTMLFormElement, data: Record<string, unknown>, replaceTyped: boolean): void {
  for (const [name, value] of Object.entries(data)) {
    const field = form.elements.namedItem(name);
    if (field instanceof HTMLInputElement && field.type === 'checkbox') {
      const typed = field.checked && field.dataset.lookupFilled !== 'true';
      if (typeof value === 'boolean' && (replaceTyped || !typed)) {
        field.checked = value;
        field.dataset.lookupFilled = 'true';
      }
      continue;
    }
    const editable = field instanceof HTMLSelectElement || (field instanceof HTMLInputElement && !field.readOnly);
    if (!editable || typeof value !== 'string') continue;
    const typed = field.value !== '' && field.dataset.lookupFilled !== 'true';
    if (typed && !replaceTyped) continue;
    field.value = value;
    field.dataset.lookupFilled = 'true';
    // Lista própria (regime): o hidden avisa o componente para o rótulo acompanhar. Sem bubbles,
    // o formulário não trata como edição da pessoa e a marca de preenchido continua.
    if (field.type === 'hidden') field.dispatchEvent(new Event('change'));
  }
}

function failureText(body: Failure): string {
  const parts = [
    body.titulo ? `${body.titulo}.` : '',
    body.corpo ?? '',
    body.proximo_passo ?? '',
    body.correlation_id ? `Código: ${body.correlation_id}` : '',
  ];
  return parts.filter(Boolean).join(' ') || COPY.semResposta;
}

function releaseTypedFields(form: HTMLFormElement): void {
  if (form.dataset.lookupForm === 'true') return;
  form.dataset.lookupForm = 'true';
  const release = (event: Event): void => {
    if (event.target instanceof HTMLElement) delete event.target.dataset.lookupFilled;
  };
  form.addEventListener('input', release);
  form.addEventListener('change', release);
}

export function bindCompanyLookup(): void {
  document.querySelectorAll<HTMLInputElement>('[data-lookup-input]').forEach((input) => {
    const kind = input.dataset.lookupInput;
    const form = input.form;
    const url = input.dataset.lookupUrl;
    if (input.dataset.bound === 'true' || (kind !== 'cnpj' && kind !== 'cep') || !form || !url) return;
    const status = form.querySelector<HTMLElement>(`[data-lookup-status="${kind}"]`);
    const trigger = form.querySelector<HTMLButtonElement>(`[data-lookup-trigger="${kind}"]`);
    if (!status || !trigger) return;

    input.dataset.bound = 'true';
    releaseTypedFields(form);
    status.hidden = false;
    trigger.hidden = false;
    const hint = status.textContent ?? '';
    // Valor já presente (reenvio com erro, edição) não dispara consulta sozinho.
    let last = normalize(kind, input.value);
    let controller: AbortController | null = null;
    let busy = false;

    // aria-disabled em vez de disabled: o foco de teclado continua no botão.
    const settle = (): void => {
      controller = null;
      busy = false;
      trigger.removeAttribute('aria-disabled');
    };

    const run = async (value: string): Promise<void> => {
      controller?.abort();
      const current = new AbortController();
      controller = current;
      busy = true;
      last = value;
      trigger.setAttribute('aria-disabled', 'true');
      show(status, COPY[kind].buscando);
      try {
        const response = await fetch(url + encodeURIComponent(value), {
          headers: { Accept: 'application/json' },
          credentials: 'same-origin',
          signal: current.signal,
        });
        if (response.redirected && new URL(response.url).pathname === '/entrar') {
          show(status, COPY.sessao, true);
          return;
        }
        const body = (await response.json()) as Record<string, unknown>;
        // Resposta atrasada de um número que a pessoa já mudou não preenche nada.
        if (normalize(kind, input.value) !== value) return;
        if (!response.ok) {
          show(status, failureText(body as Failure), true);
          return;
        }
        fill(form, body, kind === 'cep');
        const situation = typeof body.situation === 'string' ? body.situation : '';
        if (kind === 'cnpj' && situation && situation !== 'ATIVA') {
          show(status, `Atenção: a Receita Federal informa situação ${situation} para este CNPJ. Preenchemos os dados; confira antes de gravar.`, true);
        } else {
          show(status, COPY[kind].pronto);
        }
      } catch (error) {
        if (error instanceof DOMException && error.name === 'AbortError') return;
        show(status, COPY.semResposta, true);
      } finally {
        if (controller === current) settle();
      }
    };

    input.addEventListener('input', (event) => {
      const value = normalize(kind, input.value);
      // Preenchimento por código (diálogo de edição do tomador) não consulta: só digitação da
      // pessoa troca o endereço salvo pelo da consulta.
      if (!event.isTrusted) {
        last = value;
        return;
      }
      if (value.length === LENGTH[kind]) {
        if (value !== last) void run(value);
        return;
      }
      last = '';
      if (controller) {
        controller.abort();
        settle();
        show(status, hint);
      }
    });
    trigger.addEventListener('click', () => {
      if (busy) return;
      const value = normalize(kind, input.value);
      if (value.length !== LENGTH[kind]) {
        show(status, COPY[kind].incompleto, true);
        input.focus();
        return;
      }
      void run(value);
    });
  });
}
